import {Component, OnInit, inject, DestroyRef, effect, signal, untracked} from '@angular/core';
import {Router} from "@angular/router";
import {ComponentType} from "@angular/cdk/portal";
import {MatDialog, MatDialogConfig, MatDialogRef} from "@angular/material/dialog";
import {DialogRestService} from "../../core/cms/services/dialog.rest.service";
import {AccountService} from "../../core/account/services/account.rest.service";
import {LicenseData} from "../../core/account/models/license.data";
import {UserService} from "../../core/user/services/user.service";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {take} from "rxjs/operators";
import {LegalComponent} from "./legal/legal.component";
import {UpdateComponent} from "./update/update.component";
import {NormalComponent} from "./normal/normal.component";
import {CommunicationComponent} from "./communication/communication.component";
import {RequalificationComponent} from "./requalification/requalification.component";
import {EInvoiceComponent} from "./e-invoice/e-invoice.component";
import {DialogTextModel} from "../../core/cms/models/dialog.text.model";
import {UserRestService} from "../../core/user/services/user.rest.service";
import {LogService} from "../../core/base/log.service";

interface DialogConfiguration {
  component: ComponentType<unknown>;
  config: MatDialogConfig;
}

@Component({
  selector: 'app-popup',
  templateUrl: './popup.component.html',
  styleUrls: ['./popup.component.scss'],
  standalone: false
})
export class PopupComponent implements OnInit {
  private readonly shownDialogsStorageKey = 'shownDialog';
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly dialogRestService = inject(DialogRestService);
  private readonly accountService = inject(AccountService);
  private readonly userService = inject(UserService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly userRestService = inject(UserRestService);
  private readonly log = inject(LogService);
  private readonly activeDialog = signal<MatDialogRef<unknown> | null>(null);
  private readonly pendingPopup = signal<DialogTextModel | null>(null);
  private activeIdf: string | null = null;

  private readonly dialogConfigurations: Record<string, DialogConfiguration> = {
    'LEGAL': {
      component: LegalComponent,
      config: {
        width: '800px',
        height: '700px',
        disableClose: true,
        autoFocus: false
      }
    },
    'UPDATE': {
      component: UpdateComponent,
      config: {
        maxWidth: '600px',
        disableClose: true,
        autoFocus: false
      }
    },
    'POPUP': {
      component: NormalComponent,
      config: {
        maxWidth: '600px',
        autoFocus: false
      }
    },
    'COMMUNICATION': {
      component: CommunicationComponent,
      config: {
        maxWidth: '600px',
        disableClose: false,
        autoFocus: false
      }
    },
    'E_INVOICE': {
      component: EInvoiceComponent,
      config: {
        width: '700px',
        autoFocus: false
      }
    },
    'REQUALIFICATION': {
      component: RequalificationComponent,
      config: {
        maxWidth: '600px',
        autoFocus: false
      }
    }
  };

  constructor() {
    this.destroyRef.onDestroy(() => this.activeDialog()?.close());
    effect(onCleanup => {
      const idf = this.userService.selectedIdf();
      const popup = this.pendingPopup();

      untracked(() => {
        if (this.activeIdf !== idf) {
          this.activeDialog()?.close();
          this.activeDialog.set(null);
          this.activeIdf = null;
        }
        if (!popup || !idf) return;
        if (popup.dialog_popup_type === 'REQUALIFICATION') {
          if (!this.userService.isOwner() || this.hasShownDialogInSession(popup, idf)) {
            this.pendingPopup.set(null);
            return;
          }
          const request = this.accountService.loadAllAccountLicenseData(idf).pipe(take(1)).subscribe({
            next: response => {
              this.pendingPopup.set(null);
              if (response.error_code !== null) {
                this.log.error('Unable to load requalification status', response.error_code);
                return;
              }
              if (this.canShowRequalificationPopup(response.return_object)) {
                this.openDialog({...popup, idf});
                this.markDialogAsShownInSession(popup, idf);
              }
            },
            error: error => {
              this.pendingPopup.set(null);
              this.log.error('Unable to load requalification status', error);
            }
          });
          onCleanup(() => request.unsubscribe());
          return;
        }
        if (popup.dialog_popup_type !== 'E_INVOICE' && popup.dialog_popup_type !== 'COMMUNICATION') {
          this.pendingPopup.set(null);
          this.openDialog({...popup, idf});
          return;
        }
        const request = this.userRestService.getDetailsForIdf(idf).pipe(take(1)).subscribe({
          next: response => {
            this.pendingPopup.set(null);
            if (response.error_code !== null || !response.return_object) {
              this.log.error('Unable to load pharmacy details for popup', response.error_code);
              return;
            }
            this.openDialog({
              ...popup, idf,
              email: response.return_object.email,
              eInvoiceEmail: response.return_object.einvoice_email || response.return_object.email
            });
          },
          error: error => {
            this.pendingPopup.set(null);
            this.log.error('Unable to prepare popup', error);
          }
        });
        onCleanup(() => request.unsubscribe());
      });
    });
  }

  ngOnInit(): void {
    this.getDialog(this.router.url);
  }

  private getDialog(urlFilter: string): void {
    this.dialogRestService.getDialogs(urlFilter)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: popups => {
          if (popups.error_code !== null) {
            this.log.error('Unable to load popups', popups.error_code);
            return;
          }
          const selectedPopup = popups.return_object?.[0];
          if (selectedPopup) {
            this.prepareDialog(selectedPopup);
          }
        },
        error: error => this.log.error('Unable to load popups', error)
      });
  }

  private prepareDialog(selectedPopup: DialogTextModel): void {
    this.pendingPopup.set(selectedPopup);
  }


  private canShowRequalificationPopup(licenseData: LicenseData | null): boolean {
    if (!licenseData || licenseData.confirmed) {
      return false;
    }

    return this.isRequalificationDue(licenseData.requalification);
  }

  private isRequalificationDue(requalification: string | null): boolean {
    if (!requalification) {
      return false;
    }

    const dueDate = new Date(requalification);

    if (Number.isNaN(dueDate.getTime())) {
      return false;
    }

    const today = new Date();
    dueDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);

    return dueDate.getTime() <= today.getTime();
  }

  private hasShownDialogInSession(selectedPopup: DialogTextModel, currentIdf: string): boolean {
    const shownDialogs = this.getShownDialogs();
    return shownDialogs.includes(this.getShownDialogStorageEntry(selectedPopup, currentIdf));
  }

  private markDialogAsShownInSession(selectedPopup: DialogTextModel, currentIdf: string): void {
    const shownDialogs = new Set(this.getShownDialogs());
    shownDialogs.add(this.getShownDialogStorageEntry(selectedPopup, currentIdf));
    sessionStorage.setItem(this.shownDialogsStorageKey, Array.from(shownDialogs).join(','));
  }

  private getShownDialogs(): string[] {
    const shownDialogs = sessionStorage.getItem(this.shownDialogsStorageKey);

    if (!shownDialogs) {
      return [];
    }

    return shownDialogs.split(',').map(dialog => dialog.trim()).filter(Boolean);
  }

  private getShownDialogStorageEntry(selectedPopup: DialogTextModel, currentIdf: string): string {
    return `${selectedPopup.id}_${currentIdf}`;
  }

  private openDialog(selectedPopup: DialogTextModel): void {
    const dialogType = selectedPopup.dialog_popup_type;
    const dialogConfiguration = dialogType ? this.dialogConfigurations[dialogType] : undefined;

    if (dialogConfiguration) {
      const dialogRef = this.dialog.open(dialogConfiguration.component, {
        ...dialogConfiguration.config,
        data: selectedPopup
      });
      this.activeIdf = selectedPopup.idf ?? null;
      this.activeDialog.set(dialogRef);
      dialogRef.afterClosed().pipe(take(1), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
        if (this.activeDialog() === dialogRef) {
          this.activeDialog.set(null);
          this.activeIdf = null;
        }
      });
    } else {
      this.log.warn('Unsupported popup type', dialogType);
    }
  }
}
